<script>
  import { tick } from "svelte";
  import Icon from "@iconify/svelte";
  import closeIcon from "@iconify/icons-mdi/close";
  import checkIcon from "@iconify/icons-mdi/check-circle";
  import alertIcon from "@iconify/icons-mdi/alert-circle";
  import hljs from "highlight.js/lib/core";
  import jsonLang from "highlight.js/lib/languages/json";
  import "highlight.js/styles/github-dark.min.css";
  import { t } from "./lib/i18n.js";

  hljs.registerLanguage("json", jsonLang);

  export let webhook = null;
  export let locale = "en";
  export let onClose = () => {};

  /** True if webhook has any common signature header (backend sets webhook.validated) */
  function hasSignatureHeader() {
    const h = webhook?.headers || {};
    return Object.entries(h).some(([k, v]) => /x-(hub-)?(webhook-)?signature(-256)?/i.test(k) && v);
  }

  function formatJSON(str) {
    try {
      const parsed = JSON.parse(str);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return str;
    }
  }

  function getMethodClass(method) {
    const m = method?.toLowerCase();
    return `method-${m}`;
  }

  function formatTimestamp(ts) {
    return new Date(ts).toLocaleString();
  }

  const TRUNCATE_LEN = 80;
  let expandedHeaderKeys = {};
  function toggleHeader(key) {
    expandedHeaderKeys = { ...expandedHeaderKeys, [key]: !expandedHeaderKeys[key] };
  }

  let bodyCodeEl = null;
  let lastHighlightedId = null;
  $: if (webhook?.id && webhook?.is_json && webhook.id !== lastHighlightedId) {
    lastHighlightedId = webhook.id;
    (async () => {
      await tick();
      if (!bodyCodeEl) return;
      try {
        bodyCodeEl.textContent = formatJSON(webhook.body || "{}");
        delete bodyCodeEl.dataset.highlighted;
        hljs.highlightElement(bodyCodeEl);
      } catch {}
    })();
  }
  $: if (!webhook?.id) lastHighlightedId = null;
</script>

<div class="ui-card-inner h-full flex flex-col">
  <!-- Header -->
  <div class="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
    <div class="flex items-center gap-3 min-w-0">
      <span class={`method-badge ${getMethodClass(webhook?.method)}`}>
        {webhook?.method}
      </span>
      <span class="font-mono text-sm text-white truncate">{webhook?.path}</span>
    </div>
    <button
      type="button"
      class="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
      onclick={onClose}
      title={t(locale, "close")}
    >
      <Icon icon={closeIcon} width="18" height="18" />
    </button>
  </div>

  <!-- Content -->
  <div class="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
    {#if webhook}
      <!-- Metadata -->
      <div class="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span class="text-zinc-500">{t(locale, "webhook_timestamp")}</span>
          <div class="text-white">{formatTimestamp(webhook.timestamp)}</div>
        </div>
        <div>
          <span class="text-zinc-500">{t(locale, "webhook_source_ip")}</span>
          <div class="text-white">{webhook.source_ip}</div>
        </div>
        <div>
          <span class="text-zinc-500">{t(locale, "webhook_size")}</span>
          <div class="text-white">{webhook.body_size} bytes</div>
        </div>
        <div>
          <span class="text-zinc-500">{t(locale, "webhook_user_agent")}</span>
          <div class="text-white text-xs truncate">{webhook.user_agent || t(locale, "webhook_na")}</div>
        </div>
        {#if webhook.origin}
          <div class="col-span-2">
            <span class="text-zinc-500">{t(locale, "webhook_origin")}</span>
            <div class="text-white text-xs break-all">{webhook.origin}</div>
          </div>
        {/if}
      </div>

      <!-- Rejected (backend responded with reject status) -->
      {#if webhook.rejected}
        <div class="border-t border-white/10 pt-4">
          <div class="text-xs text-zinc-500 uppercase tracking-wide mb-2">{t(locale, "webhook_response")}</div>
          <div class="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-400">
            <Icon icon={alertIcon} width="16" height="16" />
            <span>{t(locale, "webhook_rejected_with", { status: webhook.rejected_status || 412 })}</span>
          </div>
        </div>
      {/if}

      <!-- Config at process time (reject + signing snapshot from Redis when this webhook was processed) -->
      {#if webhook.config_at_process}
        {@const cap = webhook.config_at_process}
        <div class="border-t border-white/10 pt-4">
          <div class="text-xs text-zinc-500 uppercase tracking-wide mb-2">{t(locale, "webhook_config_at_process")}</div>
          <div class="space-y-2 text-sm">
            <div>
              <span class="text-zinc-500">{t(locale, "webhook_reject")}</span>
              <span class="text-zinc-300 ml-2">{cap.reject_set ? (cap.reject_value ? t(locale, "webhook_on", { status: cap.reject_reason || "412" }) : t(locale, "webhook_off")) : t(locale, "webhook_not_set_off")}</span>
            </div>
            <div>
              <span class="text-zinc-500">{t(locale, "webhook_signing")}</span>
              <span class="text-zinc-300 ml-2">
                {#if cap.secret_set}
                  <span>{t(locale, "webhook_header")} <code class="bg-white/10 px-1 rounded">{cap.sig_header || "X-Hub-Signature-256"}</code></span>
                  {#if cap.secret}
                    <span class="text-zinc-400">, {t(locale, "webhook_secret")} <code class="bg-white/10 px-1 rounded break-all">{cap.secret}</code></span>
                  {/if}
                {:else}
                  {t(locale, "webhook_not_set")}
                {/if}
              </span>
            </div>
          </div>
        </div>
      {/if}

      <!-- Signature (validated by backend when a secret is set in Settings) -->
      <div class="border-t border-white/10 pt-4">
        <div class="text-xs text-zinc-500 uppercase tracking-wide mb-2">{t(locale, "webhook_signature")}</div>
        {#if !hasSignatureHeader()}
          <div class="text-sm text-zinc-500">{t(locale, "webhook_no_signature")}</div>
        {:else if webhook.config_at_process && !webhook.config_at_process.secret_set}
          <div class="rounded-lg bg-zinc-500/10 border border-zinc-500/30 px-3 py-2 text-sm text-zinc-400">
            {t(locale, "webhook_signing_not_configured")}
          </div>
        {:else if webhook.validated === true}
          <div class="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm text-emerald-400">
            <Icon icon={checkIcon} width="16" height="16" />
            <span>{t(locale, "webhook_signature_valid")}</span>
          </div>
        {:else}
          <div class="flex items-center gap-2 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/30 px-3 py-2 text-sm text-fuchsia-400">
            <Icon icon={alertIcon} width="16" height="16" />
            <span>{t(locale, "webhook_signature_invalid")}</span>
          </div>
        {/if}
      </div>

      <!-- Headers -->
      <div class="border-t border-white/10 pt-4">
        <div class="space-y-1 max-h-[200px] overflow-y-auto">
          {#each Object.entries(webhook.headers || {}) as [key, value]}
            {@const isLong = (value || "").length > TRUNCATE_LEN}
            {@const expanded = expandedHeaderKeys[key]}
            <div class="text-xs py-1">
              <span class="text-zinc-500 font-mono">{key}:</span>
              {#if isLong && !expanded}
                <span class="text-zinc-300 font-mono ml-2 break-all">{value.slice(0, TRUNCATE_LEN)}…</span>
                <button type="button" class="text-zinc-400 hover:text-white underline ml-2" onclick={() => toggleHeader(key)}>{t(locale, "live_show_more")}</button>
              {:else}
                <span class="text-zinc-300 font-mono ml-2 break-all">{value}</span>
                {#if isLong}
                  <button type="button" class="text-zinc-400 hover:text-white underline ml-2" onclick={() => toggleHeader(key)}>{t(locale, "live_show_less")}</button>
                {/if}
              {/if}
            </div>
          {/each}
        </div>
      </div>

      <!-- Body -->
      <div class="border-t border-white/10 pt-4 min-h-0">
        {#if webhook.is_json}
          <pre class="hljs w-full p-3 bg-black/40 border border-white/10 rounded-lg text-xs font-mono text-zinc-300 overflow-x-auto overflow-y-auto whitespace-pre-wrap max-h-[400px]"><code bind:this={bodyCodeEl} class="language-json"></code></pre>
        {:else}
          <pre class="w-full p-3 bg-black/40 border border-white/10 rounded-lg text-xs font-mono text-zinc-300 overflow-x-auto overflow-y-auto whitespace-pre-wrap max-h-[400px]">{webhook.body || "(empty)"}</pre>
        {/if}
      </div>

      <!-- ID -->
      <div class="border-t border-white/10 pt-4">
        <div class="text-xs text-zinc-500">{t(locale, "webhook_id")}: <span class="text-zinc-400 font-mono">{webhook.id}</span></div>
      </div>
    {/if}
  </div>
</div>
